// src/hooks/useFHIRTokens.js
//
// Drives the full authenticator-based HBA + FHIR token flow for FHIRAccountPage.
//
// Step sequence inside fetchTokens():
//   1. 'challenge'     — GET <homeserver>/owner-authenticate for challengePath + state
//   2. 'waiting'       — window.open(authenticator://) + wait for BroadcastChannel
//   3. 'exchanging'    — GET <redirectUri>?code=<authCode>&state=<state> → HBA token
//   4. 'fhir'          — Matrix OpenID → /tim-authenticate → FHIR token
//   5. 'visibility'    — GET /owner/PractitionerRole (uses cached HBA token)
//
// fetchTokens auto-runs on mount and is also exposed for manual re-fetch
// (retry / refresh buttons). If both HBA and FHIR tokens are already cached
// (e.g. user navigated away and came back), steps 1-4 are skipped.
//
// setVisibility(visible) calls fhirService.setGeneralVisibility() and refreshes.

import { useState, useCallback, useRef, useEffect } from 'react';
import fhirService from '../services/fhirService';
import hbaAuthService from '../services/hbaAuthService';
import {
  isMockAuthenticatorEnabled,
  isMockAuthenticatorHostAllowed,
} from '../utils/devToggles';
import logger from '../utils/logger';

// Module-scope helper — pure, no state dependency.
const isStatus = (err, status) => err?.status === status || (err?.cause?.status === status);

/**
 * @typedef {'idle'|'challenge'|'waiting'|'exchanging'|'fhir'|'visibility'|'ready'|'error'} FHIRStep
 *
 * @typedef {object} FHIRState
 * @property {FHIRStep} step
 * @property {string|null} error
 * @property {boolean} isRegistered
 * @property {boolean} isGenerallyVisible
 * @property {boolean} isUpdating
 * @property {string|null} updateError
 */

export function useFHIRTokens() {
  const [state, setState] = useState({
    step: 'idle',
    error: null,
    isRegistered: false,
    isGenerallyVisible: false,
    isVisibleToInsurees: false,
    isUpdating: false,
    updateError: null,
  });

  const visibilityStateRef = useRef(null);
  // Separate controllers so a visibility toggle does not abort an in-flight
  // fetchTokens (and vice versa). authAbortRef covers the auth flow + mount
  // visibility load; updateAbortRef covers visibility toggles.
  const authAbortRef = useRef(null);
  const updateAbortRef = useRef(null);

  const fetchTokens = useCallback(async () => {
    authAbortRef.current?.abort();
    hbaAuthService.cancelPendingAuth();
    const controller = new AbortController();
    authAbortRef.current = controller;
    const { signal } = controller;

    const setStep = (step) => setState(prev => ({ ...prev, step, error: null }));

    // Skip authenticator + FHIR-token steps when both tokens are already
    // cached. On cached-path failure below we clear the cache so the next
    // retry runs full re-auth rather than retrying with the same likely-
    // server-rejected token.
    const usedCachedTokens = fhirService.hasValidHbaToken() && fhirService.hasValidFhirToken();

    try {
      if (!usedCachedTokens) {
        // Use the homeserver URL — the homeserver proxies the VZD owner-authenticate
        // endpoint per gematik spec, so this works across TU/RU/PU environments
        // without env-var hardcoding.
        const baseUrl = fhirService.getBaseUrl();
        const flagOn = isMockAuthenticatorEnabled();
        const useMock = flagOn && isMockAuthenticatorHostAllowed(baseUrl);
        if (flagOn && !useMock) {
          logger.warn('[useFHIRTokens] Mock Authenticator flag set but homeserver host not in allowlist — falling back to real authenticator flow.');
        }

        if (!useMock) {
          // Step 1: Fetch challenge from VZD FHIR directory
          setStep('challenge');
          const { challenge_path, state: authState, redirect_uri: challengeRedirectUri } = await hbaAuthService.getAuthChallenge(baseUrl, signal);

          if (signal.aborted) return;

          // Step 2: Open authenticator deep link, wait for auth code via BroadcastChannel.
          // callback=tim tells the authenticator to call back via tim://code=<code>&state=<state>&cardType=HBA.
          // For the web app, the redirect lands at /hba (or /#/hba); src/index.jsx
          // detects this pre-React, posts { code, state } via BroadcastChannel,
          // scrubs the URL, and auto-closes the tab.
          setStep('waiting');
          // Prefer redirect_uri from challenge response/JWT claims; fall back to extracting
          // it from the challenge_path URL query params; last resort: our /hba origin.
          const fallbackUri = `${window.location.origin}/hba`;
          const redirectUri = challengeRedirectUri
            || hbaAuthService.extractRedirectUri(challenge_path, fallbackUri);
          hbaAuthService.launchAuthenticator(challenge_path);
          const { code: authCode, state: returnedState } = await hbaAuthService.waitForAuthCode(signal);

          if (signal.aborted) return;

          // OAuth state CSRF check: if the callback supplied a state, it must match
          // the state we issued. (If the callback page didn't propagate state, we
          // skip — defense-in-depth, not enforcement, given BroadcastChannel is
          // already same-origin scoped.)
          if (returnedState && authState && returnedState !== authState) {
            throw new Error('OAuth state mismatch — rejecting auth code.');
          }

          // Step 3: Exchange auth code for HBA token.
          // Prefer the state echoed back by the authenticator over the one we
          // parsed from the challenge — the VZD signin endpoint requires the
          // round-tripped value, and some challenge JWTs don't carry a `state`
          // claim, leaving authState undefined.
          setStep('exchanging');
          const hbaTokenData = await hbaAuthService.exchangeAuthCode(redirectUri, authCode, returnedState ?? authState, signal);

          if (signal.aborted) return;

          // Inject HBA token into fhirService cache so all owner-API calls use it
          fhirService.setHbaToken(hbaTokenData.access_token, hbaTokenData.expires_in);
          logger.info('[useFHIRTokens] HBA token acquired and cached');
        } else {
          // Mock branch — getOwnVisibility() lazily calls fhirService.getHbaToken(),
          // which hits ${homeserver}/mock-authenticator and populates the HBA cache.
          // Do NOT call setHbaToken here: it would skip the lazy path and require
          // a duplicate request to mint a token to inject.
          logger.info('[useFHIRTokens] Mock Authenticator toggle ON — skipping deep-link flow');
          if (signal.aborted) return;
        }

        // Step 4: Fetch FHIR token (Matrix OpenID → /tim-authenticate)
        setStep('fhir');
        await fhirService.getFhirToken(signal);

        if (signal.aborted) return;
      }

      // Step 5: Fetch practitioner visibility (uses cached HBA token)
      setStep('visibility');
      const visibility = await fhirService.getOwnVisibility(signal);

      if (signal.aborted) return;

      logger.info('[useFHIRTokens] Visibility fetched', {
        isRegistered: visibility.isRegistered,
        isGenerallyVisible: visibility.isGenerallyVisible,
      });

      visibilityStateRef.current = visibility;
      setState({
        step: 'ready',
        error: null,
        isRegistered: visibility.isRegistered,
        isGenerallyVisible: visibility.isGenerallyVisible,
        isVisibleToInsurees: visibility.isVisibleToInsurees,
        isUpdating: false,
        updateError: null,
      });
    } catch (err) {
      if (signal.aborted || err.name === 'AbortError') return;
      logger.error('[useFHIRTokens] Flow failed:', err);
      // Clear cached tokens on cached-path failure so retry forces re-auth
      // rather than looping on the same server-rejected token.
      if (usedCachedTokens) {
        logger.warn('[useFHIRTokens] Cached-token path failed; clearing tokens — retry will re-authenticate');
        fhirService.clearTokens();
      }
      setState(prev => ({
        ...prev,
        step: 'error',
        error: err.message || 'Failed to load FHIR account data',
      }));
    }
  }, []);

  // Auto-start the flow on mount.
  useEffect(() => {
    fetchTokens();
    return () => {
      authAbortRef.current?.abort();
      updateAbortRef.current?.abort();
      hbaAuthService.cancelPendingAuth();
    };
  }, [fetchTokens]);

  // Shared implementation for setVisibility / setInsureeVisibility. The two
  // public callbacks differ only in which fhirService method they call and
  // the log label.
  const runVisibilityUpdate = useCallback(async (updateFn, logLabel) => {
    if (!visibilityStateRef.current) return;

    updateAbortRef.current?.abort();
    const controller = new AbortController();
    updateAbortRef.current = controller;
    const { signal } = controller;

    setState(prev => ({ ...prev, isUpdating: true, updateError: null }));

    try {
      try {
        await updateFn(visibilityStateRef.current, signal);
      } catch (firstErr) {
        if (signal.aborted || firstErr.name === 'AbortError') throw firstErr;
        if (!isStatus(firstErr, 410)) throw firstErr;
        // Stale visibility state — refresh and retry once.
        logger.warn(`[useFHIRTokens] ${logLabel}: stale state (410), refreshing and retrying`);
        const refreshed = await fhirService.getOwnVisibility(signal);
        if (signal.aborted) return;
        visibilityStateRef.current = refreshed;
        await updateFn(refreshed, signal);
      }
      if (signal.aborted) return;

      const updated = await fhirService.getOwnVisibility(signal);
      if (signal.aborted) return;

      visibilityStateRef.current = updated;
      setState(prev => ({
        ...prev,
        isGenerallyVisible: updated.isGenerallyVisible,
        isVisibleToInsurees: updated.isVisibleToInsurees,
        isUpdating: false,
        updateError: null,
      }));
    } catch (err) {
      if (signal.aborted || err.name === 'AbortError') return;
      logger.error(`[useFHIRTokens] ${logLabel} failed:`, err);
      setState(prev => ({
        ...prev,
        isUpdating: false,
        updateError: err.message || 'Failed to update visibility',
      }));
    }
  }, []);

  const setVisibility = useCallback((visible) => runVisibilityUpdate(
    (state, signal) => fhirService.setGeneralVisibility(visible, state, signal),
    'setVisibility',
  ), [runVisibilityUpdate]);

  const setInsureeVisibility = useCallback((visible) => runVisibilityUpdate(
    (state, signal) => fhirService.setInsureeVisibility(visible, state, signal),
    'setInsureeVisibility',
  ), [runVisibilityUpdate]);

  return { state, fetchTokens, setVisibility, setInsureeVisibility };
}
